/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import { useKidsContext } from "@packages/kids";
import type { AdminDashUserResponse } from "@packages/sdk";
import { useRequestAdminDashboardResetPassword } from "@packages/sdk";
import {
  ModalDropdownMenuItem,
  ReplayIcon,
  spacing,
  useAlert,
  useModal,
} from "@packages/ui";

import { ResetPasswordModal } from "./ResetPasswordModal";

const dropdownMenuItemContentStyles: Interpolation = {
  alignItems: "center",
  columnGap: spacing.s,
  display: "flex",
};

export const ResetPasswordMenuItem = ({
  user,
  communityId,
  screenName,
}: {
  user: AdminDashUserResponse;
  communityId: number;
  screenName: "members" | "class_detail";
}) => {
  const modal = useModal(ResetPasswordModal);
  const setPassword = useRequestAdminDashboardResetPassword();
  const t = useTranslations();
  const { setToast } = useKidsContext();
  const { track } = useAnalytics();
  const alert = useAlert();

  // users created in the HAD only have passwords if magic link is disabled OR they're not using email
  const canReset =
    (!!user.email && user.is_magic_link_auth_disabled) ||
    (!user.email && !!user.username);

  const handleSelect = async () => {
    track({
      event: HallowAnalyticsEvent.TappedResetPassword,
      properties: {
        auth_type: !!user.email ? "email" : "username",
        screen_name: screenName,
      },
    });
    const values: { password?: string } | undefined = await modal.show({
      user,
    });
    if (!values?.password) return;
    try {
      await setPassword.mutateAsync({
        body: { password: values.password },
        params: { path: { community_id: communityId, user_id: user.id } },
      });
      setToast(
        t("web_admin_dash_password_reset_successfully_toast"),
        "success",
      );
    } catch (error) {
      await alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  if (!canReset) return null;

  return (
    <ModalDropdownMenuItem
      onSelect={handleSelect}
      modalItemProps={{
        onClick: (e) => e.stopPropagation(),
      }}
      dropdownMenuItemProps={{
        onClick: (e) => e.stopPropagation(),
      }}
    >
      <div css={dropdownMenuItemContentStyles}>
        <ReplayIcon />
        <I18nMessage message="reset_pw_title" />
      </div>
    </ModalDropdownMenuItem>
  );
};
